import { Link } from 'react-router-dom';
import { HelpCircle, ArrowRight, MessageCircle, Phone } from 'lucide-react';
import { SEO } from '../components/common';
import { Button, Accordion } from '../components/ui';
import { FAQ_ITEMS, CONTACT_INFO } from '../utils/constants';

/**
 * FAQ page component
 */
const FAQPage = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'FAQ', url: '/faq' },
  ];

  return (
    <>
      <SEO
        title="MRI FAQ - Common Questions About MRI Scans"
        description="Find answers to frequently asked questions about MRI scans at Mian Tufail Diagnostic Center Kasur. Learn about MRI preparation, safety, procedure duration, Open MRI for claustrophobic patients, appointment booking, 6-8 hour report delivery, and emergency report delivery within 2 hours."
        keywords="MRI FAQ, MRI questions answers, how to prepare for MRI, is MRI safe, MRI scan duration, Open MRI claustrophobia, MRI appointment booking Kasur, MRI report delivery time, 6-8 hour MRI reports, emergency MRI report delivery, MRI insurance Pakistan"
        url="/faq"
        breadcrumbs={breadcrumbs}
        faqItems={FAQ_ITEMS}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-2xl mb-6">
              <HelpCircle className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-6">
              Frequently Asked <span className="gradient-text">Questions</span>
            </h1>
            <p className="text-lg text-neutral-600 leading-relaxed">
              Find answers to common questions about our services, MRI procedures, 
              and how to book your appointment.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <Accordion items={FAQ_ITEMS} />
          </div>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-16 bg-neutral-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-soft border border-neutral-100">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-neutral-600">
                  Can&apos;t find the answer you&apos;re looking for? Our friendly team is 
                  here to help you with any questions about our services.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Call Us Card */}
                <div className="bg-primary-50 rounded-xl p-6">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                    <Phone className="w-6 h-6 text-primary-600" />
                  </div>
                  <h3 className="font-semibold text-neutral-900 mb-2">Call Us</h3>
                  <p className="text-neutral-600 text-sm mb-4">
                    Speak directly with our team for immediate assistance.
                  </p>
                  <a href={`tel:${CONTACT_INFO.phone}`}>
                    <Button variant="primary" size="sm" icon={Phone} fullWidth>
                      {CONTACT_INFO.phoneDisplay}
                    </Button>
                  </a>
                </div>

                {/* WhatsApp Card */}
                <div className="bg-green-50 rounded-xl p-6">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                    <MessageCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-neutral-900 mb-2">WhatsApp Us</h3>
                  <p className="text-neutral-600 text-sm mb-4">
                    Send us a message for quick responses to your queries.
                  </p>
                  <a
                    href={CONTACT_INFO.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="whatsapp" size="sm" icon={MessageCircle} fullWidth>
                      Chat on WhatsApp
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary-700">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Book Your Appointment?
          </h2>
          <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto">
            Now that you have all the information, schedule your MRI scan today.
          </p>
          <Link to="/book-appointment">
            <Button variant="accent" size="lg" icon={ArrowRight} iconPosition="right">
              Book Appointment
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default FAQPage;
